import { useTranslation } from "react-i18next";
import BottomPanel from "../../app/panels/BottomPanel";
import {
  closeAnnotation,
  useAnnotationSheet,
  type AnnotationTarget,
  type AnnotationView,
} from "../../../domains/gpt/stores/annotationSheetStore";
import { RateAnswerView } from "./views/RateAnswerView";
import { SkipTurnView } from "./views/SkipTurnView";
import { CompareAnswerView } from "./views/CompareAnswerView";

function renderView(
  view: AnnotationView,
  target: AnnotationTarget
): React.JSX.Element {
  switch (view.view) {
    case "rate":
      return <RateAnswerView target={target} />;
    case "skip":
      return <SkipTurnView target={target} />;
    case "compare":
      return <CompareAnswerView target={target} />;
    default:
      throw new Error(view satisfies never);
  }
}

/**
 * The expert-annotation sheet, mounted once app-wide next to `SettingsSheet` and
 * opened imperatively from a chat answer's action bar.
 *
 * One SCROLLING sheet rather than a multi-screen wizard: the rubric is seven
 * criteria and the PRD's own open question notes they do not fit a phone screen —
 * a wizard would turn a 90-second task into seven transitions. The two views that
 * do replace it are the ones that are genuinely a different question (passing, and
 * comparing against another tool).
 *
 * NO slide animation between the views, and that is a repair rather than an
 * omission. It used to cross-fade them inside an `absolute inset-0` layer, and
 * that layer is what broke the sheet: `BottomPanel`'s content slot is ALREADY the
 * scroll container, so an absolutely-positioned child of it has no definite
 * height, the views added a second `overflow-y-auto` to compensate, and the result
 * was two overlapping scrollbars and a footer that sat at the end of the content
 * instead of pinned — seven criteria to scroll through before the buttons existed
 * at all. One scroll container and a `sticky` footer are worth more than a
 * transition.
 *
 * `disableBottomPadding` for the same reason: the sticky footer has to reach the
 * panel's bottom edge, not float 20px above it.
 */
export function AnnotationSheet(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { open, target, stack } = useAnnotationSheet();

  // `target` outlives `open` on purpose, so the form does not unmount while the
  // panel slides out; before the sheet has ever been opened there is nothing to
  // render.
  if (target === null) return null;

  // `.at(-1)`, not `[length - 1]`: the index form types as always-present, so the
  // guard below would be dead code that still fires on an empty stack.
  const active = stack.at(-1);
  if (active === undefined) {
    throw new Error("should not happen: empty annotation view stack");
  }

  const title =
    active.view === "compare" ? t("Compare") : t("Evaluate this answer");

  return (
    <BottomPanel
      isOpen={open}
      onClose={closeAnnotation}
      height="92dvh"
      title={title}
      displayCloseButton
      disableBottomPadding
      // The views now swap inside ONE scroll box (that is the whole point of the
      // layout repair), so without this the second view opens at the scroll
      // position the first was left at — you submit at the bottom of the rubric
      // and land mid-page in the comparison.
      scrollResetKey={active.view}
    >
      {renderView(active, target)}
    </BottomPanel>
  );
}
